/* eslint-disable no-unused-vars */
import React from 'react';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { GetJson, PostJson, DeleteJson, PutJson } from '@awp/helpers/fetchReact';
import { REST_API, STATE_STATUS, FAKE_SEPARATOR, BLANK_VIEW_NAME, GRAPH_COLORS } from '@awp/constants';
import objectHash from 'object-hash';
import AwpToastContent from '@components/Common/AwpToastContent';
import { ToastContainer, toast } from 'react-toastify';
import Color from 'color';

const initialState = {
  graphViewId: null,
  graphViews: [],
  viewableEndDate: {
    value: Date.now(),
    error: '',
    disabled: false,
  },
  showInvalidData: false,
  showGraphLegends: true,
  syncGraphs: true,
  selectedDatasets: {},
  status: {
    graphViews: STATE_STATUS.IDLE,
    saveGraphView: STATE_STATUS.IDLE,
    deleteGraphView: STATE_STATUS.IDLE,
    updateGraphView: STATE_STATUS.IDLE,
    error: '',
  },
};

const blankDataGraph = {
  DataGraphId: 0,
  PersistentGraphFilters: [],
  DisplayOrder: 0,
};

const blankView = {
  Id: 0,
  Name: BLANK_VIEW_NAME,
  SecurityIdentifierId: null,
  IsSystem: false,
  IsModified: true,
  IsCustom: true,
  //IsUnsaved: true,
  DataGraphs: [],
};

// Weather Station state from DV
export const currentWeatherStation = (state) => state.dataVisualization.weatherStation;
export const currentWeatherStationId = (state) => Number.parseInt(state.dataVisualization.weatherStationId, 10);

// Selected Dataset state, used by dropdown
export const selectSelectedDatasets = (state) => state.graphViewPage.selectedDatasets;

//Graph View
export const currentGraphView = (state) => selectAllGraphViews(state).find((gv) => gv.Id == currentGraphViewId(state));
export const currentGraphViewId = (state) => state.graphViewPage.graphViewId;
export const selectAllGraphViews = (state) => state.graphViewPage.graphViews;

// Thunk states
export const selectStatus = (state) => state.graphViewPage.status;
export const selectViewSaveState = (state) => state.graphViewPage.status.saveGraphView;
export const selectViewDeleteState = (state) => state.graphViewPage.status.deleteGraphView;
export const selectViewUpdateState = (state) => state.graphViewPage.status.updateGraphView;
export const shouldResetGraphViewsZoom = (state) => state.graphViewPage.resetGraphViewsZoom;

// Viewable End Date controls the graphs visible portal
export const getViewableEndDate = (state) => state.graphViewPage.viewableEndDate;
// Invalid Data toggle
export const shouldShowInvalidData = (state) => state.graphViewPage.showInvalidData;
// All Graph Legend show/hide toggle
export const shouldShowGraphLegends = (state) => state.graphViewPage.showGraphLegends;

// these are older selectors that I didn't touch
export const shouldSyncGraphs = (state) => state.graphViewPage.syncGraphs;

/**
 * Thunks
 */
export const fetchGraphViews = createAsyncThunk('graphViewPage/fetchGraphViews', async () => {
  let url = REST_API.GRAPH_VIEWS;
  const response = await GetJson(url);
  return await response.json();
});

export const saveGraphView = createAsyncThunk('graphViewPage/saveGraphView', async (payload, { getState }) => {
  const { graphViewId, graphName } = payload;

  let url = REST_API.GRAPH_VIEWS;

  const state = getState();

  const graphViewToSave = {
    Name: graphName,
    Description: 'User Graph View',
    DataGraphs: state.graphViewPage.graphViews.find((gv) => gv.Id == graphViewId).DataGraphs || [],
  };

  const response = await PostJson(url, graphViewToSave);

  if (response.ok) {
    return { response: await response.json(), graphViewId: graphViewId };
  }

  return { response: null, error: await response.json(), graphViewId: graphViewId };
});

export const deleteGraphView = createAsyncThunk('graphViewPage/deleteGraphView', async (graphViewId) => {
  let url = REST_API.GRAPH_VIEWS;
  url += `${graphViewId}`;

  await DeleteJson(url, undefined);
  return { graphViewId: graphViewId };
});

// if matchedGraphViewId is passed in then we are overwriting an existing view
export const updateGraphView = createAsyncThunk('graphViewPage/updateGraphView', async (payload, { getState }) => {
  const { graphViewId, matchedGraphViewId } = payload;

  let url = REST_API.GRAPH_VIEWS;

  const state = getState();
  //current graph
  const graphView = state.graphViewPage.graphViews.find((gv) => gv.Id == graphViewId);
  //check if we are overwriting a graph
  const matchedGraphView = state.graphViewPage.graphViews.find((gv) => gv.Id == matchedGraphViewId);

  //if we are overwriting we use all the base graph view details but the DataGraphs from the current view
  const graphViewToSave = matchedGraphViewId
    ? {
        Id: matchedGraphView.Id,
        Name: matchedGraphView.Name,
        Concurrency: matchedGraphView.Concurrency,
        DataGraphs: graphView.DataGraphs,
      }
    : {
        Id: graphView.Id,
        Name: graphView.Name,
        Concurrency: graphView.Concurrency,
        DataGraphs: graphView.DataGraphs,
      };

  const response = await PutJson(url, graphViewToSave);

  return { response: await response.json(), graphViewId: matchedGraphViewId ? matchedGraphViewId : graphViewId };
});

/**
 * SELECTORS
 */
export const getSelectedDatasetOptions = createSelector(
  [(state) => state.graphViewPage.selectedDatasets, (_, graphKey) => graphKey],
  (selectedDatasets, graphKey) => {
    return selectedDatasets[graphKey] || [];
  },
);

export const selectAllGraphViewsSorted = createSelector([selectAllGraphViews], (allGraphViews) => {
  //separate the system from the user graphs
  const systemGraphViews = allGraphViews
    .filter((gv) => gv.IsSystem === true)
    // WEAV-7116 moved sorting from API, to handle sorting when adding views
    .sort((a, b) => {
      return a.Name < b.Name ? -1 : a.Name > b.Name ? 1 : 0;
    });

  const userGraphViews = allGraphViews
    .filter((gv) => gv.IsSystem === false)
    // WEAV-7116 moved sorting from API, to handle sorting when adding views
    .sort((a, b) => {
      return a.Name < b.Name ? -1 : a.Name > b.Name ? 1 : 0;
    });

  //generate a blank view that will be transformed into a separator between system and custom
  if (userGraphViews.length > 0)
    systemGraphViews.push({
      ...blankDataGraph,
      Name: FAKE_SEPARATOR,
      IsSystem: true,
      Id: objectHash.MD5(Math.random().toString(36).substr(2, 9)),
    });

  return systemGraphViews.concat(userGraphViews);
});

export const getGraphViewName = createSelector([currentGraphView], (graphView) => {
  return (graphView || { Name: '' }).Name;
});

export const isSystemGraphView = createSelector([currentGraphView], (graphView) => {
  return (graphView || { IsSystem: false }).IsSystem;
});

export const isGraphViewUnsaved = createSelector([currentGraphView], (graphView) => {
  return (graphView || { IsUnsaved: false }).IsUnsaved;
});

export const isGraphViewModified = createSelector([currentGraphView], (graphView) => {
  return (graphView || { IsModified: false }).IsModified;
});

/**
 * SLICE
 */
export const graphViewPageSlice = createSlice({
  name: 'graphViewPage',
  initialState,
  reducers: {
    unselectSelectedDataset(state, action) {
      const { datasetId, group, obsId, graphKey } = action.payload;
      //get our graph view
      const currentGraphView = state.graphViews.find((gv) => gv.Id == state.graphViewId);
      //find which graph we are updating
      const graphIdx = currentGraphView.DataGraphs.findIndex((graph) => graph.DataGraphId.toString() === graphKey);

      //check if the selected dataset is in the graph
      const dsIdx = currentGraphView.DataGraphs[graphIdx].PersistentGraphFilters.findIndex(
        ({ PlacementName, ObservationTypeId }) =>
          PlacementName === group && parseInt(ObservationTypeId) === parseInt(obsId),
      );
      if (dsIdx != -1) {
        //remove the dataset from graph view
        currentGraphView.DataGraphs[graphIdx].PersistentGraphFilters.splice(dsIdx, 1);
      }

      //find the dataset value in selected datasets and remove it
      const removingIdx = state.selectedDatasets[graphKey].findIndex((ds) => ds.value === datasetId);
      if (removingIdx != -1) {
        state.selectedDatasets[graphKey].splice(removingIdx, 1);
      }

      //graph view is dirty, IsModified is now true until saved
      currentGraphView.IsModified = true;
    },
    setSelectedDatasets(state, action) {
      const { options, graphId, isUpdateAction } = action.payload;
      //get our graph view
      const currentGraphView = state.graphViews.find((gv) => gv.Id == state.graphViewId);
      //find which graph we are updating
      const graphIdx = currentGraphView.DataGraphs.findIndex((graph) => graph.DataGraphId.toString() === graphId);

      //we need to sync the selected options with the persistent graph filter
      if (isUpdateAction || false) {
        const selOpts = state.selectedDatasets[graphId].map((e) => {
          return { ...e };
        });

        const removedOpts = selOpts.filter((s) => !options.some((o) => o.value === s.value));
        const addedOpts = options.filter((o) => !selOpts.some((s) => s.value === o.value));

        if (addedOpts.length >= 1) {
          //add options to the persistent graph filter
          addedOpts.map((o) => {
            currentGraphView.DataGraphs[graphIdx].PersistentGraphFilters.push({
              DataGraphId: graphId,
              PlacementName: o.group,
              ObservationTypeId: o.typeId,
            });
          });
        }

        if (removedOpts.length >= 1) {
          removedOpts.map((o) => {
            const dsIdx = currentGraphView.DataGraphs[graphIdx].PersistentGraphFilters.findIndex(
              ({ PlacementName, ObservationTypeId }) =>
                PlacementName === o.name && parseInt(ObservationTypeId) === parseInt(o.obstypeid),
            );
            if (dsIdx != -1) {
              //remove the dataset from graph view
              currentGraphView.DataGraphs[graphIdx].PersistentGraphFilters.splice(dsIdx, 1);
            }
          });
        }

        currentGraphView.IsModified = true;
      }
      // we only want to set the group name and obsTypeId for each selected option
      // this will allow us to easily change weather stations and keep the same options
      // selected if they exist.
      let selectedOptions = options.map((o, i) => {
        return {
          name: o.group,
          obstypeid: o.typeId,
          value: o.value,
          label: o.label,
          color: o.color || GRAPH_COLORS[i % GRAPH_COLORS.length],
          sortLabel: `${o.label} (${o.group})`,
        };
      });

      const groupedOpts = Object.values(
        selectedOptions.reduce((acc, curr) => {
          if (!acc[curr.obstypeid]) {
            acc[curr.obstypeid] = [curr];
          } else {
            acc[curr.obstypeid].push(curr);
          }
          return acc;
        }, {}),
      )
        .map((groups) => {
          const sortedGroup = groups.sort((a, b) => {
            return a.sortLabel.localeCompare(b.sortLabel);
          });
          return sortedGroup;
        })
        .flatMap((objs) => {
          let { h, s, l } = Color(objs[0].color).hsl().object();
          const origL = l;
          let tintAdj = Math.round(Math.floor(85 - l) / objs.length, 1);

          return objs.map((obj) => {
            //clamp the lightness to 90, so that it resets back to default
            // reusing shades if users select more than 4 observations
            if (Math.min(l, 90) > 90) {
              l = origL;
            }

            obj.color = Color({ h, s, l }).hex().toLowerCase();

            l += tintAdj;
            return obj;
          });
        });

      state.selectedDatasets[graphId] = selectedOptions;
    },
    moveGraph(state, action) {
      const { graphId, viewAction } = action.payload;

      const currentGraphView = state.graphViews.find((gv) => gv.Id == state.graphViewId);
      const graphIdx = currentGraphView.DataGraphs.findIndex((graph) => graph.DataGraphId.toString() === graphId);

      let newIdx = graphIdx;
      if (viewAction == 'down') {
        newIdx += 1;
        //validate new idx doesn't exceed array len, if so then set at max
        newIdx = newIdx > currentGraphView.DataGraphs.length - 1 ? currentGraphView.DataGraphs.length - 1 : newIdx;
      }
      if (viewAction == 'up') {
        newIdx -= 1;
        //don't go lower than zero or it'll start splicing at the end of array
        newIdx = newIdx < 0 ? 0 : newIdx;
      }

      arrayMove(currentGraphView.DataGraphs, graphIdx, newIdx);
      reorderIndexProperty(currentGraphView.DataGraphs, 'DisplayOrder');
      currentGraphView.IsModified = true;
    },
    addGraphByPosition(state, action) {
      const { graphId, viewAction } = action.payload;

      //find the current graph view that action was requested for
      const currentGraphView = state.graphViews.find((gv) => gv.Id == state.graphViewId);
      const graphIdx = currentGraphView.DataGraphs.findIndex((graph) => graph.DataGraphId.toString() === graphId);

      const newGraph = { ...blankDataGraph, DataGraphId: (currentGraphView.DataGraphs.length + 1) * -1 }; //objectHash.MD5(Math.random().toString(36).substr(2, 9))

      if (viewAction == 'addAbove') {
        currentGraphView.DataGraphs.splice(graphIdx, 0, newGraph);
      }
      if (viewAction == 'addBelow') {
        currentGraphView.DataGraphs.splice(graphIdx + 1, 0, newGraph);
      }
      if (viewAction == 'addBottom') {
        currentGraphView.DataGraphs = [...currentGraphView.DataGraphs, newGraph];
      }

      reorderIndexProperty(currentGraphView.DataGraphs, 'DisplayOrder');
      currentGraphView.IsModified = true;
    },
    removeGraph(state, action) {
      const { graphId } = action.payload;

      //find the current graph view that action was requested for
      const currentGraphView = state.graphViews.find((gv) => gv.Id == state.graphViewId);
      const graphIdx = currentGraphView.DataGraphs.findIndex((graph) => graph.DataGraphId.toString() === graphId);

      //fail out if we didn't find the graph to remove, otherwise code will just remove the last one
      if (graphIdx == -1) return;

      currentGraphView.DataGraphs.splice(graphIdx, 1);

      reorderIndexProperty(currentGraphView.DataGraphs, 'DisplayOrder');
      currentGraphView.IsModified = true;
    },
    generateNewGraphView(state) {
      //find existing new graphs with the Blank name
      const blankViews = state.graphViews.filter((gv) => {
        return gv.Name.startsWith(BLANK_VIEW_NAME) && gv.IsCustom;
      });

      const newGraphView = {
        ...blankView,
        DataGraphs: [],
        Id: (blankViews.length + 1) * -1,
        Name: BLANK_VIEW_NAME + ' ' + padDigits(blankViews.length + 1, 2),
      };

      //generate a new graph and give it a temp graph id using a object hash
      newGraphView.DataGraphs.push({
        ...blankDataGraph,
        DataGraphId: -1, //objectHash.MD5(Math.random().toString(36).substr(2, 9)),
      });

      reorderIndexProperty(newGraphView.DataGraphs, 'DisplayOrder');

      state.graphViews.push(newGraphView);
      state.graphViewId = newGraphView.Id;
    },
    resetGraphViewToDefault(state) {
      const graphViewIdx = state.graphViews.findIndex((gv) => {
        return gv.Id == state.graphViewId;
      });

      if (state.graphViewId > 0) {
        // reset the graph view to it's default
        state.graphViews[graphViewIdx] = {
          ...state.graphViews[graphViewIdx],
          IsModified: false,
          DataGraphs: JSON.parse(state.graphViews[graphViewIdx].defaultGraphView),
        };
      } else {
        //the graph view is a temp 'new' view, we can remove it
        state.graphViews.splice(graphViewIdx, 1);
      }
    },
    setGraphViewId(state, action) {
      state.graphViewId = action.payload;
    },
    toggleInvalidData(state, action) {
      return { ...state, showInvalidData: action.payload || !state.showInvalidData };
    },
    toggleShowGraphLegend(state, action) {
      return { ...state, showGraphLegends: action.payload || !state.showGraphLegends };
    },
    toggleGraphSync(state, action) {
      state.syncGraphs = action.payload || !state.syncGraphs;
    },
    setViewableEndDate(state, action) {
      state.viewableEndDate = action.payload;
    },
  },
  extraReducers: {
    [fetchGraphViews.pending]: (state) => {
      state.status.graphViews = STATE_STATUS.LOADING;
    },
    [fetchGraphViews.fulfilled]: (state, action) => {
      let graphViews = action.payload;
      state.status.graphViews = STATE_STATUS.SUCCESS;

      state.graphViews = graphViews.map((gv) => {
        return { ...gv, defaultGraphView: JSON.stringify(gv.DataGraphs) };
      });
    },
    [fetchGraphViews.rejected]: (state, action) => {
      state.status.graphViews = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [saveGraphView.pending]: (state) => {
      state.status.saveGraphView = STATE_STATUS.LOADING;
    },
    [saveGraphView.fulfilled]: (state, action) => {
      const { response, error, graphViewId } = action.payload;

      //failure path
      if (!response) {
        state.status.saveGraphView = STATE_STATUS.FAILED;

        if (error) {
          console.error(error);
          state.status.error = { ...error };
          if (error.entity) {
            toast(<AwpToastContent title={'Could not save graph view !'} msgList={error.entity} />, {
              autoClose: false,
            });

            return;
          }
        }

        return;
      }

      //happy path
      state.status.saveGraphView = STATE_STATUS.SUCCESS;
      //get the index of the graph we're operating on
      const graphViewIdx = state.graphViews.findIndex((gv) => {
        return gv.Id == graphViewId;
      });

      const newGraphView = response;
      //determine if we're removing a temp graph or not
      const removeCount = state.graphViews[graphViewIdx].IsSystem || false || graphViewId > 0 ? 0 : 1;
      //it was a system graph that was cloned, reset it
      if (removeCount === 0) {
        const systemGraphView = state.graphViews[graphViewIdx];

        state.graphViews[graphViewIdx] = {
          ...systemGraphView,
          DataGraphs: JSON.parse(systemGraphView.defaultGraphView),
          IsModified: false,
        };
      }

      //remove the temp graph
      state.graphViews.splice(graphViewIdx, removeCount, {
        ...newGraphView,
        defaultGraphView: JSON.stringify(newGraphView.DataGraphs),
      });

      //set the graphId to the new one
      state.graphViewId = newGraphView.Id;

      toast(<AwpToastContent title={'Graph view successfully saved.'} isErr={false} />, {
        autoClose: true,
      });
    },
    [saveGraphView.rejected]: (state, action) => {
      state.status.saveGraphView = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [deleteGraphView.pending]: (state) => {
      state.status.deleteGraphView = STATE_STATUS.LOADING;
    },
    [deleteGraphView.fulfilled]: (state, action) => {
      state.status.deleteGraphView = STATE_STATUS.SUCCESS;
      const { graphViewId } = action.payload;

      //find the graphView that was deleted and remove it from the state
      const graphViewIdx = state.graphViews.findIndex((gv) => {
        return gv.Id == graphViewId;
      });

      state.graphViews.splice(graphViewIdx, 1);

      toast(<AwpToastContent title={'Graph view successfully deleted.'} isErr={false} />, {
        autoClose: true,
      });
    },
    [deleteGraphView.rejected]: (state, action) => {
      state.status.deleteGraphView = STATE_STATUS.FAILED;
    },
    [updateGraphView.pending]: (state) => {
      state.status.updateGraphView = STATE_STATUS.LOADING;
    },
    [updateGraphView.rejected]: (state, action) => {
      state.status.updateGraphView = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [updateGraphView.fulfilled]: (state, action) => {
      const { response, error, graphViewId } = action.payload;

      //failure path
      if (!response) {
        state.status.updateGraphView = STATE_STATUS.FAILED;

        if (error) {
          console.error(error);
          state.status.error = { ...error };
          if (error.entity) {
            toast(<AwpToastContent title={'Could not save graph view !'} msgList={error.entity} />, {
              autoClose: false,
            });

            return;
          }
        }

        return;
      }

      //happy path
      state.status.updateGraphView = STATE_STATUS.SUCCESS;
      //get the index of the graph we're operating on
      const graphViewIdx = state.graphViews.findIndex((gv) => {
        return gv.Id == graphViewId;
      });

      //update the graph with the changes
      const updatedGraphView = response;
      state.graphViews[graphViewIdx] = {
        ...state.graphViews[graphViewIdx],
        Concurrency: updatedGraphView.Concurrency,
        IsModified: false,
        DataGraphs: updatedGraphView.DataGraphs,
        defaultGraphView: JSON.stringify(updatedGraphView.DataGraphs),
      };

      //ensure the graphId is set to the updated one
      state.graphViewId = graphViewId;

      toast(<AwpToastContent title={'Graph view successfully updated.'} isErr={false} />, {
        autoClose: true,
      });
    },
  },
});

/**
 * EXPOSED REDUCERS
 */
export const {
  toggleInvalidData,
  toggleGraphSync,
  toggleShowGraphLegend,
  setGraphViewId,
  setSelectedDatasets,
  unselectSelectedDataset,
  moveGraph,
  addGraphByPosition,
  removeGraph,
  resetGraphViewToDefault,
  setViewableEndDate,
  generateNewGraphView,
} = graphViewPageSlice.actions;

/**
 * HELPER FUNCTIONS
 */
const arrayMove = (array, fromIdx, toIdx) => {
  let curElement = array[fromIdx];
  array.splice(fromIdx, 1);
  array.splice(toIdx, 0, curElement);
};

const reorderIndexProperty = (array, propertyName) => {
  array.map((e, i) => {
    e[propertyName] = i + 1;
  });
};

function padDigits(n, width, z) {
  z = z || '0';
  n = n + '';
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}
